import type { VariantProps } from 'cva'

import { cn, cva } from '~/utils/classnames'

import { Slot, Slottable } from './slot'
import { Spinner } from './spinner'

const buttonStyle = cva({
  base: [
    'relative whitespace-nowrap inline-flex items-center justify-center gap-1.5 font-medium transition focus-visible:outline-none disabled:opacity-40 disabled:pointer-events-none enabled:cursor-pointer active:scale-98',
    'text-(--button-text-color)',
    'in-data-[button-group]:not-first:not-last:not-only:rounded-none in-data-[button-group]:not-only:first:rounded-r-none in-data-[button-group]:not-only:last:rounded-l-none in-data-[button-group]:active:scale-100 in-data-[button-group]:active:opacity-95',
    'ring-accent-element/50',
  ],
  variants: {
    variant: {
      primary: [
        'bg-accent-primary [--button-text-color:var(--color-accent-foreground)] hover:bg-accent-secondary active:bg-accent-tertiary data-[active=true]:bg-accent-secondary',
        'in-data-[button-group]:not-only:not-last:border-r in-data-[button-group]:border-current/16',
      ],
      'over-media': [
        'bg-under/64 backdrop-blur-md [--button-text-color:var(--color-highlight)] hover:bg-under/80 active:bg-under data-[active=true]:bg-under/80',
        'in-data-[button-group]:not-only:not-last:border-r in-data-[button-group]:border-current/16',
      ],
      outline: [
        'border border-border/8 bg-transparent hover:bg-under/4 active:bg-under/6 [--button-text-color:var(--color-foreground)] focus-visible:border-accent-element focus-visible:z-[1] data-[active=true]:bg-under/4',
        'in-data-[button-group]:not-only:not-first:border-l-0',
        // TODO: add invalid state to remaining variants
        'data-invalid:border-destructive data-invalid:hover:border-destructive data-invalid:focus-visible:border-destructive data-invalid:focus-visible:ring-destructive/20!',
      ],
      ghost:
        'bg-transparent hover:bg-under/4 active:bg-under/6 [--button-text-color:var(--color-foreground)] data-[active=true]:bg-under/4',
    },
    size: {
      xs: 'rounded-sm px-1.5 text-xs h-5',
      sm: 'rounded-md px-3 text-xs h-8',
      md: 'rounded-md px-4 text-sm h-9',
      lg: 'rounded-lg px-5 text-base h-12',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

export interface ButtonProps
  extends
    React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonStyle> {
  asChild?: boolean
  isLoading?: boolean
  'data-active'?: boolean
}

const ButtonRoot = ({
  children,
  className,
  variant,
  asChild = false,
  isLoading,
  size = 'md',
  type = 'button',
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      className={cn(
        buttonStyle({
          className,
          variant,
          size,
        }),
        isLoading && 'text-transparent transition-none'
      )}
      ref={ref}
      type={type}
      {...props}
    >
      <Slottable asChild={asChild} child={children}>
        {(child) => (
          <>
            {child}
            {isLoading && (
              <span
                data-button-spinner
                className={cn(
                  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'text-(--button-text-color)'
                )}
              >
                <Spinner size={size} />
              </span>
            )}
          </>
        )}
      </Slottable>
    </Comp>
  )
}

const ButtonGroup = ({
  className,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return (
    <div
      data-button-group
      className={cn('flex items-center whitespace-nowrap', className)}
      {...props}
    />
  )
}

const IconButton = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ButtonRoot> & {
  'aria-label': string
}) => {
  return (
    <ButtonRoot
      className={cn('aspect-square px-0 [&>svg]:shrink-0', className)}
      {...props}
    />
  )
}

const Button = Object.assign(ButtonRoot, {
  Group: ButtonGroup,
})

export { Button, IconButton, buttonStyle }
